import type { MeResponse, PublicConfigResponse } from '@vlastiq/contracts';
import { ensureAuthToken, getAuthToken } from '../telegram/auth.ts';
import { isMiniApp } from '../telegram/launch-params.ts';
import { toApiError } from './error.ts';

// `ApiError` itself lives in ./error.ts so the Mini App's token exchange can
// throw one without a circular import. Re-exported here because this is where
// every page already imports it from.
export { ApiError } from './error.ts';

/**
 * Shared fetch wrapper.
 *
 * Exported as `apiRequest` so the Phase 2 data client reuses exactly this
 * error handling rather than growing a second, subtly different one. The Mini
 * App's `Authorization` header is injected here for the same reason — one
 * place, so no call site can be the one that forgets.
 */
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  // Inside a Mini App the cookie may never arrive (Telegram Web frames the
  // app, and a SameSite=Lax cookie is not sent cross-site), so the token is
  // obtained BEFORE the first request rather than after the first 401.
  if (isMiniApp() && getAuthToken() === null) await ensureAuthToken();

  let response = await send(path, init, getAuthToken());

  /**
   * Silent re-exchange, once.
   *
   * The token lives in memory, so a reload loses it by definition and the
   * first request after one is guaranteed to 401. `initData` is still in
   * sessionStorage, so this is invisible to the member.
   *
   * Exactly once: a second 401 after a fresh token means the credential is not
   * the problem, and looping would turn one expired session into an unbounded
   * request storm against `/auth/miniapp`.
   */
  if (response.status === 401 && isMiniApp()) {
    const refreshed = await ensureAuthToken(true);
    if (refreshed !== null) response = await send(path, init, refreshed);
  }

  if (!response.ok) throw await toApiError(response);

  return (await response.json()) as T;
}

function send(path: string, init: RequestInit | undefined, token: string | null) {
  return fetch(path, {
    // The session cookie is first-party (same origin serves API and SPA), but
    // being explicit costs nothing and survives a future origin split.
    credentials: 'same-origin',
    ...init,
    // AFTER the spread, or a caller passing `headers` would silently replace
    // the merged defaults rather than add to them.
    headers: {
      Accept: 'application/json',
      // Every write in this app sends JSON; setting it here means callers
      // never have to remember, and Hono's c.req.json() never sees a body it
      // refuses to parse.
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      // DECISION-32. Absent outside a Mini App, where the cookie is the
      // credential and adding a header would only widen what gets logged.
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      ...init?.headers,
    },
  });
}

export const fetchMe = () => apiRequest<MeResponse>('/api/me');

export const fetchPublicConfig = () => apiRequest<PublicConfigResponse>('/api/public-config');

export const postLogout = () => apiRequest<{ ok: true }>('/auth/logout', { method: 'POST' });

/** Development only; the server returns 404 for this in production. */
export const postDevLogin = (telegramUserId: number) =>
  apiRequest<{ ok: true }>('/auth/dev-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ telegramUserId }),
  });
